import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Badge, Alert, Button, Modal, Form, InputGroup, ListGroup, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push, remove, query, orderByChild, limitToLast } from 'firebase/database';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import '../styles/calendar.css';
import { FaRunning, FaBiking, FaDumbbell, FaCalendarAlt, FaListUl, FaEdit, FaTrash, FaPlus } from 'react-icons/fa'; 
import WorkoutList from './WorkoutList';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import './styles.css';
import type { TooltipDataAttrs } from 'react-calendar-heatmap';

interface Workout {
  id: string;
  date: string;
  duration: number; 
  exercises: Exercise[];
  notes?: string;
}

interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number; 
  notes?: string;
}

// Define ExerciseTemplate interface
interface ExerciseTemplate {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number;
  notes?: string;
}

// Interfaces for Workout Templates
interface WorkoutTemplateExercise {
  id?: string; // Optional: if exercises are globally unique and referenced from a central store
  name: string;
  defaultSets?: number;    // Renamed from sets
  defaultReps?: number;    // Renamed from reps
  defaultWeight?: number;  // Renamed from weight
  notes?: string; // General notes or instructions for this type of exercise in the template
  order?: number; // New field for ordering exercises
}

interface WorkoutTemplate {
  id: string;
  name: string;
  description?: string;
  exercises: WorkoutTemplateExercise[];
}

interface CurrentWizardExerciseData {
  name: string; // From template, non-editable in this step typically
  sets: number;
  reps: number;
  weight: number;
  notes?: string;
}

interface HeatmapValue {
  date: string;
  count: number; // This will represent workout intensity
}

const WorkoutDashboard = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showModal, setShowModal] = useState(false);
  const [currentWorkout, setCurrentWorkout] = useState<Workout>({
    id: '',
    date: new Date().toISOString().split('T')[0],
    duration: 60,
    exercises: []
  });
  const [exercises, setExercises] = useState<Exercise[]>([]); 
  const [newExercise, setNewExercise] = useState<Exercise>({
    id: '',
    name: '',
    sets: 3,
    reps: 10,
    weight: 0,
    notes: ''
  });
  const [exerciseTemplates, setExerciseTemplates] = useState<ExerciseTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(''); // Initialize with empty string
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type?: string, message: string } | null>(null);

  // --- Wizard State ---
  const [isWizardMode, setIsWizardMode] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0: Template Selection, 1..N: Exercise Config
  const [workoutTemplates, setWorkoutTemplates] = useState<WorkoutTemplate[]>([]);
  const [currentWizardExerciseIndex, setCurrentWizardExerciseIndex] = useState(0);
  const [wizardExerciseData, setWizardExerciseData] = useState<Exercise[]>([]);
  // State for the current exercise form in the wizard
  const [currentWizardExerciseFormData, setCurrentWizardExerciseFormData] = useState<CurrentWizardExerciseData>({ name: '', sets: 0, reps: 0, weight: 0, notes: '' });
  // --- End Wizard State ---

  const [wizardStartTime, setWizardStartTime] = useState<number | null>(null);
  const [activityChartData, setActivityChartData] = useState<HeatmapValue[]>([]);
  const [showWorkoutsForDateModal, setShowWorkoutsForDateModal] = useState(false);
  const [selectedHeatmapDate, setSelectedHeatmapDate] = useState<string>('');
  const [workoutsForSelectedDate, setWorkoutsForSelectedDate] = useState<Workout[]>([]);

  useEffect(() => {
    if (!currentUser) return;

    const workoutsRef = ref(database, `workouts/${currentUser.uid}/workouts`);
    
    // Function to calculate workout intensity and prepare heatmap data
    const calculateWorkoutIntensity = (workoutsData: Record<string, any>) => {
      const intensityByDate: Record<string, number> = {};
      
      Object.entries(workoutsData).forEach(([id, workout]: [string, any]) => {
        const date = workout.date;
        let totalIntensity = 0;
        
        if (workout.exercises && Array.isArray(workout.exercises)) {
          workout.exercises.forEach((exercise: Exercise) => {
            // Calculate intensity as sets * reps * weight
            const exerciseIntensity = exercise.sets * exercise.reps * exercise.weight;
            totalIntensity += exerciseIntensity;
          });
        }
        
        // Round intensity to a whole number
        totalIntensity = Math.round(totalIntensity);
        
        // Add to existing date or create new entry
        if (intensityByDate[date]) {
          intensityByDate[date] += totalIntensity;
        } else {
          intensityByDate[date] = totalIntensity;
        }
      });
      
      // Convert to heatmap format
      return Object.entries(intensityByDate).map(([date, intensity]) => ({
        date,
        count: intensity
      }));
    };
    const unsubscribeWorkouts = onValue(workoutsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const workoutsList = Object.entries(data).map(([id, workout]: [string, any]) => ({
        id,
        ...(workout as Omit<Workout, 'id'>)
      })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setWorkouts(workoutsList);
      
      // Calculate and set activity chart data
      if (data) {
        const intensityData = calculateWorkoutIntensity(data);
        setActivityChartData(intensityData);
      }
    });

    const exerciseTemplatesRef = ref(database, `users/${currentUser.uid}/exercise_templates`);
    const unsubscribeExerciseTemplates = onValue(exerciseTemplatesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const templatesList = Object.entries(data).map(([id, template]: [string, any]) => ({
        id,
        ...(template as Omit<ExerciseTemplate, 'id'>) // Corrected cast here
      }));
      setExerciseTemplates(templatesList); // This should now be type-correct
    });

    const workoutTemplatesRefPath = `workouts/${currentUser.uid}/workout-templates`;
    const workoutTemplatesRef = ref(database, workoutTemplatesRefPath);
    const unsubscribeWorkoutTemplates = onValue(workoutTemplatesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const templates: WorkoutTemplate[] = Object.entries(data).map(([id, tpl]: [string, any]) => ({
        id,
        name: tpl.name || 'Unbenannte Vorlage',
        description: tpl.description || '',
        exercises: tpl.exercises || [],
      }));
      setWorkoutTemplates(templates);
      console.log('Workout Templates loaded: ', templates);
    }, (error) => {
      console.error(`Error fetching workout templates from ${workoutTemplatesRefPath}:`, error);
      setAlertMessage({type: 'danger', message: 'Fehler beim Laden der Workout-Vorlagen.'});
    });

    if (location.state?.message) {
      setAlertMessage({ message: location.state.message, type: location.state.type || 'info' });
      navigate(location.pathname, { replace: true, state: {} });
    }

    if (location.state?.editWorkoutId && workouts.length > 0) { 
      const workoutIdToEdit = location.state.editWorkoutId;
      const workoutToEdit = workouts.find(w => w.id === workoutIdToEdit);
      if (workoutToEdit) {
        setCurrentWorkout(workoutToEdit);
        setExercises(workoutToEdit.exercises || []);
        setShowModal(true);
        navigate(location.pathname, { replace: true, state: { ...location.state, editWorkoutId: null } });
      }
    }

    return () => {
      unsubscribeWorkouts();
      unsubscribeExerciseTemplates();
      unsubscribeWorkoutTemplates(); // Unsubscribe new listener
    };
  }, [currentUser, location.state, navigate, workouts]); 

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3) {
        const localDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return localDate.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    const dateString = date.toISOString().split('T')[0];
    const existingWorkout = workouts.find(w => w.date === dateString);

    if (existingWorkout) {
      // Edit existing workout - current flow
      setIsWizardMode(false);
      setCurrentWorkout(existingWorkout);
      setExercises(existingWorkout.exercises || []);
    } else {
      // Add new workout - initiate wizard
      setIsWizardMode(true);
      setWizardStep(0); // Start at template selection
      setSelectedTemplateId(''); // Reset to empty string
      setCurrentWizardExerciseIndex(0);
      setWizardExerciseData([]);
      setCurrentWorkout({ // Still set currentWorkout for the date
        id: '', 
        date: dateString, 
        duration: 60, // Default duration, wizard might override or ask later
        exercises: [],
        notes: '',
      });
      setExercises([]); // Clear any old exercises from form state
    }
    setShowModal(true);
  };

  const handleSaveWorkout = async () => {
    if (!currentUser || !currentWorkout.date || currentWorkout.duration <= 0) {
      setAlertMessage({ type: 'danger', message: 'Datum und Dauer sind erforderlich.' });
      return;
    }

    const workoutDataForFirebase: Omit<Workout, 'id'> = {
      date: currentWorkout.date,
      duration: currentWorkout.duration,
      notes: currentWorkout.notes || '', 
      exercises: exercises, 
    };
    
    let workoutIdToSave: string;

    if (currentWorkout.id) { 
      workoutIdToSave = currentWorkout.id;
    } else { 
      const newWorkoutKey = push(ref(database, `workouts/${currentUser.uid}/workouts`)).key;
      if (!newWorkoutKey) {
        setAlertMessage({ type: 'danger', message: 'Fehler beim Erstellen der Workout-ID.' });
        return;
      }
      workoutIdToSave = newWorkoutKey;
    }
    
    try {
      await set(ref(database, `workouts/${currentUser.uid}/workouts/${workoutIdToSave}`), workoutDataForFirebase);
      setAlertMessage({ type: 'success', message: 'Workout erfolgreich gespeichert!' });
      setShowModal(false);
      setCurrentWorkout({ id: '', date: new Date().toISOString().split('T')[0], duration: 60, exercises: [], notes: '' });
      setExercises([]);
    } catch (error) {
      console.error('Error saving workout:', error);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Speichern des Workouts.' });
    }
  };

  const handleDeleteWorkout = async () => {
    if (!currentUser || !currentWorkout.id) return;
    try {
      await remove(ref(database, `workouts/${currentUser.uid}/workouts/${currentWorkout.id}`));
      setAlertMessage({ type: 'success', message: 'Workout erfolgreich gelöscht!' });
      setShowModal(false);
      setCurrentWorkout({ id: '', date: new Date().toISOString().split('T')[0], duration: 60, exercises: [] });
      setExercises([]);
    } catch (error) {
      console.error('Error deleting workout:', error);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Löschen des Workouts.' });
    }
  };

  const handleAddExercise = () => {
    if (!newExercise.name || newExercise.sets <= 0 || newExercise.reps <= 0) {
      setAlertMessage({type: 'warning', message: 'Name, Sätze und Wiederholungen für die Übung sind erforderlich.'});
      return;
    }
    const exerciseToAdd: Exercise = { ...newExercise, id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };
    setExercises([...exercises, exerciseToAdd]);
    setShowExerciseModal(false);
    setNewExercise({ id: '', name: '', sets: 3, reps: 10, weight: 0, notes: '' });
    setSelectedTemplateId('');
  };

  const handleEditExercise = (exercise: Exercise) => {
    setEditingExercise(exercise);
    setNewExercise(exercise);
    setShowExerciseModal(true);
  };

  const handleUpdateExercise = () => {
    if (!editingExercise || !newExercise.name || newExercise.sets <= 0 || newExercise.reps <= 0) {
        setAlertMessage({type: 'warning', message: 'Name, Sätze und Wiederholungen für die Übung sind erforderlich.'});
        return;
    }
    setExercises(exercises.map(ex => ex.id === editingExercise.id ? { ...newExercise, id: editingExercise.id } : ex));
    setShowExerciseModal(false);
    setEditingExercise(null);
    setNewExercise({ id: '', name: '', sets: 3, reps: 10, weight: 0, notes: '' });
    setSelectedTemplateId('');
  };

  const handleDeleteExercise = (id: string) => {
    setExercises(exercises.filter(ex => ex.id !== id));
  };

  const handleExerciseTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId && templateId !== 'new_exercise') {
      const template = exerciseTemplates.find(t => t.id === templateId);
      if (template) {
        setNewExercise(prev => ({
          ...prev,
          name: template.name,
          sets: template.sets || prev.sets,
          reps: template.reps || prev.reps,
          weight: template.weight || prev.weight,
          notes: template.notes || prev.notes || ''
        }));
      } else {
        setNewExercise(prev => ({ ...prev, name: '' }));
      }
    } else {
      setNewExercise(prev => ({ ...prev, name: '', notes: '' })); 
    }
  };

  const getTileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    const dateString = date.toISOString().split('T')[0];
    const workoutsOnDay = workouts.filter(w => w.date === dateString);

    if (workoutsOnDay.length > 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-1">
          {workoutsOnDay.slice(0, 3).map((_wo, index) => (
            <FaDumbbell key={index} size="0.6em" className={index > 0 ? "ms-1" : ""} />
          ))}
          {workoutsOnDay.length > 3 && <span style={{fontSize: '0.6em'}} className="ms-1">+</span>}
        </div>
      );
    }
    return null;
  };

  const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    const dateString = date.toISOString().split('T')[0];
    const workoutsOnDay = workouts.filter(w => w.date === dateString);
    if (workoutsOnDay.length > 0) {
      return 'has-workout';
    }
    return null;
  };

  // New function to save workout from wizard data
  const handleSaveWizardWorkout = async (exercisesToSave: Exercise[]) => {
    if (!currentUser || !currentWorkout.date) {
      setAlertMessage({ type: 'danger', message: 'Datum ist erforderlich.' });
      return;
    }
    if (exercisesToSave.length === 0) {
      setAlertMessage({ type: 'danger', message: 'Keine Übungen für das Workout konfiguriert.' });
      return;
    }

    const selectedTemplate = workoutTemplates.find(t => t.id === selectedTemplateId);

    let calculatedDurationMinutes = 30; // Default duration in case startTime is not set
    if (wizardStartTime) {
      const endTime = Date.now();
      const durationMilliseconds = endTime - wizardStartTime;
      calculatedDurationMinutes = Math.max(1, Math.round(durationMilliseconds / 60000)); // Ensure at least 1 minute
      setWizardStartTime(null); // Reset start time after using it
    } else {
      // Fallback estimation if wizardStartTime was not set (should not happen in normal flow)
      calculatedDurationMinutes = exercisesToSave.reduce((acc, ex) => acc + (ex.sets * 3), 0) || 30; // e.g., 3 mins per exercise set group
    }

    const workoutToSave: Omit<Workout, 'id'> = {
      date: currentWorkout.date, // Date is from initial calendar click
      duration: calculatedDurationMinutes,
      notes: currentWorkout.notes || `Workout from template: ${selectedTemplate?.name || 'Unknown Template'}`, // Use notes from currentWorkout if set, else default
      exercises: exercisesToSave, // Use the passed exercises
    };

    const newWorkoutKey = push(ref(database, `workouts/${currentUser.uid}/workouts`)).key;
    if (!newWorkoutKey) {
      setAlertMessage({ type: 'danger', message: 'Fehler beim Erstellen der Workout-ID.' });
      return;
    }

    try {
      await set(ref(database, `workouts/${currentUser.uid}/workouts/${newWorkoutKey}`), workoutToSave);
      setAlertMessage({ type: 'success', message: 'Workout erfolgreich aus Vorlage erstellt!' });
      setShowModal(false);
      // Reset wizard state
      setIsWizardMode(false);
      setWizardStep(0);
      setSelectedTemplateId('');
      setWizardExerciseData([]);
      setCurrentWizardExerciseIndex(0);
    } catch (error) {
      console.error('Error saving wizard workout:', error);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Speichern des Workouts aus Vorlage.' });
    }
  };

  const handleWizardStepChange = (currentTemplate: WorkoutTemplate | undefined) => {
    // This function is called when wizardStep > 0
    if (!currentTemplate) {
      setAlertMessage({type: 'error', message: 'Template not found during step change.'});
      return;
    }

    const currentExerciseToSave: Exercise = {
      id: currentTemplate.exercises[currentWizardExerciseIndex].id || `temp-${Date.now()}`,
      name: currentWizardExerciseFormData.name, 
      sets: currentWizardExerciseFormData.sets ?? 0, 
      reps: currentWizardExerciseFormData.reps ?? 0, 
      weight: currentWizardExerciseFormData.weight ?? 0, 
      notes: currentWizardExerciseFormData.notes,
    };
    const finalWizardExerciseData = [...wizardExerciseData];
    finalWizardExerciseData[currentWizardExerciseIndex] = currentExerciseToSave;
    setWizardExerciseData(finalWizardExerciseData); 

    const nextExerciseIndex = currentWizardExerciseIndex + 1;
    if (nextExerciseIndex < currentTemplate.exercises.length) {
      const nextExerciseTemplateDetails = currentTemplate.exercises[nextExerciseIndex];
      setCurrentWizardExerciseFormData({
        name: nextExerciseTemplateDetails.name,
        sets: nextExerciseTemplateDetails.defaultSets ?? 0,
        reps: nextExerciseTemplateDetails.defaultReps ?? 0,
        weight: nextExerciseTemplateDetails.defaultWeight ?? 0,
        notes: nextExerciseTemplateDetails.notes ?? '',
      });
      setCurrentWizardExerciseIndex(nextExerciseIndex);
      setWizardStep(wizardStep + 1);
    } else {
      // Last exercise, call save with the final data
      handleSaveWizardWorkout(finalWizardExerciseData);
    }
  };

  const getTooltipDataAttrs = (value: any) => {
    if (!value || !value.date || typeof value.count !== 'number' || value.count === 0) {
      return { 'data-tip': '' } as any as TooltipDataAttrs;
    } else {
      return { 'data-tip': `${value.date}: Intensität ${value.count}` } as any as TooltipDataAttrs;
    }
  };

  // Function to handle clicks on the heatmap cells
  const handleHeatmapClick = (value: any) => {
    if (!value || !value.date) return;
    
    const dateString = value.date;
    const workoutsForDate = workouts.filter(w => w.date === dateString);
    
    if (workoutsForDate.length > 0) {
      setSelectedHeatmapDate(dateString);
      setWorkoutsForSelectedDate(workoutsForDate);
      setShowWorkoutsForDateModal(true);
    }
  };

  const getClassForValue = (value: any) => {
    if (!value || !value.date || typeof value.count !== 'number' || value.count === 0) {
      return 'color-github-0';
    }
    
    // Scale the intensity to color levels (0-4)
    // Adjust these thresholds based on your typical workout intensity values
    if (value.count > 1000) {
      return 'color-github-4';
    } else if (value.count > 750) {
      return 'color-github-3';
    } else if (value.count > 500) {
      return 'color-github-2';
    } else if (value.count > 250) {
      return 'color-github-1';
    } else {
      return 'color-github-1';
    }
  };

  return (
    <Container className="mt-3">
      <h2 className="mb-3">Workouts</h2>

      <Nav variant="pills" className="mb-3 flex-wrap">
        <Nav.Item>
          <Nav.Link as={Link} to="/dashboard/workouts" active={location.pathname === '/dashboard/workouts' || location.pathname === '/dashboard/workouts/'}>
            <FaCalendarAlt className="me-1" /> Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={Link} to="/dashboard/workouts-list" active={location.pathname === '/dashboard/workouts-list'}>
            <FaListUl className="me-1" /> Workout Liste
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={Link} to="/dashboard/workout-exercises" active={location.pathname === '/dashboard/workout-exercises'}>
            <FaDumbbell className="me-1" /> Übungen
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {alertMessage && (
        <Alert variant={alertMessage.type || 'info'} onClose={() => setAlertMessage(null)} dismissible>
          {alertMessage.message}
        </Alert>
      )}
      <Row className="mb-4">
        <Col md={12}> 
          <Card>
            <Card.Header>
              <h4 className="mb-0"><FaCalendarAlt className="me-2" />Workout Kalender</h4>
            </Card.Header>
            <Card.Body>
              <Calendar
                onClickDay={handleDateClick}
                value={selectedDate}
                tileContent={getTileContent}
                tileClassName={getTileClassName}
                className="w-100 border-0"
                locale="de-DE"
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center heatmap-title">
                <span>Aktivitätsdiagramm</span>
              </Card.Title>
              <div className="clickable-heatmap">
                <CalendarHeatmap
                  startDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                  endDate={new Date()}
                  values={activityChartData}
                  classForValue={getClassForValue}
                  tooltipDataAttrs={getTooltipDataAttrs}
                  showMonthLabels={true}
                  showWeekdayLabels={true}
                  horizontal={true}
                  gutterSize={5}
                  weekdayLabels={['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']}
                  onClick={handleHeatmapClick}
                />
              </div>
              <div className="heatmap-legend">
                <div className="heatmap-legend-item">
                  <div className="heatmap-legend-color" style={{backgroundColor: '#ebedf0'}}></div>
                  <span>Keine</span>
                </div>
                <div className="heatmap-legend-item">
                  <div className="heatmap-legend-color" style={{backgroundColor: '#c6e48b'}}></div>
                  <span>Niedrig</span>
                </div>
                <div className="heatmap-legend-item">
                  <div className="heatmap-legend-color" style={{backgroundColor: '#7bc96f'}}></div>
                  <span>Mittel</span>
                </div>
                <div className="heatmap-legend-item">
                  <div className="heatmap-legend-color" style={{backgroundColor: '#239a3b'}}></div>
                  <span>Hoch</span>
                </div>
                <div className="heatmap-legend-item">
                  <div className="heatmap-legend-color" style={{backgroundColor: '#196127'}}></div>
                  <span>Sehr hoch</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Workout Modal */}
      <Modal 
        show={showModal} 
        onHide={() => {
          setShowModal(false);
          // Reset wizard states comprehensively when modal is hidden
          setIsWizardMode(false);
          setWizardStep(0);
          setSelectedTemplateId('');
          setCurrentWizardExerciseIndex(0);
          setCurrentWizardExerciseFormData({ name: '', sets: 0, reps: 0, weight: 0, notes: '' });
          setWizardExerciseData([]);
          setWizardStartTime(null); // Reset wizard start time
          setCurrentWorkout({ id: '', date: new Date().toISOString().split('T')[0], duration: 60, exercises: [], notes: '' });
          setExercises([]); // Clear exercises from non-wizard form too
          setAlertMessage(null); // Clear any modal-specific alerts
        }}
        size={isWizardMode ? "lg" : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>{currentWorkout.id ? 'Workout bearbeiten' : 'Neues Workout hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isWizardMode ? (
            // --- Wizard UI --- 
            <div>
              {wizardStep === 0 && (
                <Form.Group className="mb-3">
                  <Form.Label><h5>Schritt 1: Workout-Vorlage auswählen</h5></Form.Label>
                  {workoutTemplates.length === 0 && <p>Keine Workout-Vorlagen gefunden. Bitte erstelle welche.</p>}
                  <ListGroup>
                    {workoutTemplates.map(template => (
                      <ListGroup.Item 
                        key={template.id} 
                        action 
                        active={selectedTemplateId === template.id}
                        onClick={() => setSelectedTemplateId(template.id)}
                      >
                        {template.name}
                        {template.description && <div className="small text-muted">{template.description}</div>}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Form.Group>
              )}
              {/* Wizard steps for exercises */}
              {wizardStep > 0 && selectedTemplateId && (() => {
                const template = workoutTemplates.find(t => t.id === selectedTemplateId);
                if (!template || !template.exercises || currentWizardExerciseIndex >= template.exercises.length) {
                  return <p>Fehler: Übungsdetails nicht gefunden.</p>;
                }
                const exerciseTemplateDetails = template.exercises[currentWizardExerciseIndex];
                
                return (
                  <div>
                    <h5>Schritt {wizardStep + 1}: {exerciseTemplateDetails.name}</h5>
                    <p style={{ fontSize: '0.9em', color: 'gray' }}>
                      Vorlagenwerte: 
                      Sets: {exerciseTemplateDetails.defaultSets ?? 'k.A.'}, 
                      Wdh: {exerciseTemplateDetails.defaultReps ?? 'k.A.'}, 
                      Gewicht: {exerciseTemplateDetails.defaultWeight ?? 'k.A.'} kg
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>Sätze</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={currentWizardExerciseFormData.sets}
                        onChange={(e) => setCurrentWizardExerciseFormData({...currentWizardExerciseFormData, sets: parseInt(e.target.value) || 0})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Wiederholungen</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={currentWizardExerciseFormData.reps}
                        onChange={(e) => setCurrentWizardExerciseFormData({...currentWizardExerciseFormData, reps: parseInt(e.target.value) || 0})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Gewicht (kg)</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="0.25"
                        value={currentWizardExerciseFormData.weight}
                        onChange={(e) => setCurrentWizardExerciseFormData({...currentWizardExerciseFormData, weight: parseFloat(e.target.value) || 0})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Notizen (optional)</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={2}
                        value={currentWizardExerciseFormData.notes || ''}
                        onChange={(e) => setCurrentWizardExerciseFormData({...currentWizardExerciseFormData, notes: e.target.value})}
                      />
                    </Form.Group>
                  </div>
                );
              })()}
            </div>
          ) : (
            // --- Existing Form UI for Editing or Manual Add (if wizard is skipped) ---
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Datum</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={currentWorkout.date}
                      onChange={(e) => setCurrentWorkout({...currentWorkout, date: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Dauer (Minuten)</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={currentWorkout.duration}
                      onChange={(e) => setCurrentWorkout({...currentWorkout, duration: parseInt(e.target.value) || 0})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Notizen</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={2}
                  value={currentWorkout.notes || ''}
                  onChange={(e) => setCurrentWorkout({...currentWorkout, notes: e.target.value})}
                />
              </Form.Group>

              <hr />
              <h5>Übungen</h5>
              {exercises.length > 0 ? (
                <ListGroup className="mb-3">
                  {exercises.map((ex, index) => (
                    <ListGroup.Item key={ex.id || `ex-${index}`} className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{ex.name}</strong>: {ex.sets} Sätze x {ex.reps} Wdh. @ {ex.weight}kg
                        {ex.notes && <div className="small text-muted"><em>{ex.notes}</em></div>}
                      </div>
                      <div>
                        <Button variant="outline-primary" size="sm" onClick={() => handleEditExercise(ex)} className="me-2">
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteExercise(ex.id)}>
                          <FaTrash />
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p className="text-muted">Noch keine Übungen hinzugefügt.</p>
              )}
              <Button variant="success" onClick={() => { 
                  setEditingExercise(null); 
                  setNewExercise({ id: '', name: '', sets: 3, reps: 10, weight: 0, notes: '' }); 
                  setSelectedTemplateId(''); // Use empty string for no selection
                  setShowExerciseModal(true); 
              }} className="mb-3">
                <FaPlus className="me-1" /> Übung hinzufügen
              </Button>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isWizardMode ? (
            // --- Wizard Footer Buttons ---
            <> 
              {(() => {
                // Define template here to be in scope for button text and onClick handlers
                const template = workoutTemplates.find(t => t.id === selectedTemplateId);
                return (
                  <>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
                    {wizardStep > 0 && (
                      <Button variant="outline-primary" onClick={() => {
                        // 'template' is now available from the outer IIFE scope
                        if (!template) return;

                        if (wizardStep === 1) { // Going from first exercise back to template selection
                          setWizardStep(0);
                          setCurrentWizardExerciseIndex(0);
                          setCurrentWizardExerciseFormData({ name: '', sets: 0, reps: 0, weight: 0, notes: '' });
                        } else if (wizardStep > 1) { // Going from exercise N to N-1
                          const prevExerciseIndex = currentWizardExerciseIndex - 1;
                          const prevTemplateExercise = template.exercises[prevExerciseIndex];
                          setCurrentWizardExerciseFormData({
                            name: prevTemplateExercise.name,
                            sets: wizardExerciseData[prevExerciseIndex]?.sets ?? prevTemplateExercise.defaultSets ?? 0,
                            reps: wizardExerciseData[prevExerciseIndex]?.reps ?? prevTemplateExercise.defaultReps ?? 0,
                            weight: wizardExerciseData[prevExerciseIndex]?.weight ?? prevTemplateExercise.defaultWeight ?? 0,
                            notes: wizardExerciseData[prevExerciseIndex]?.notes ?? prevTemplateExercise.notes ?? '',
                          });
                          setCurrentWizardExerciseIndex(prevExerciseIndex);
                          setWizardStep(wizardStep - 1);
                        }
                      }}>Zurück</Button>
                    )}
                    <Button 
                      variant="primary" 
                      onClick={() => {
                        // 'template' is available from the outer IIFE scope
                        if (wizardStep === 0) {
                          // Logic for moving from Template Selection to First Exercise
                          if (!template) { 
                            setAlertMessage({type: 'warning', message: 'Bitte zuerst eine Vorlage auswählen.'});
                            return;
                          }

                          // Sort exercises by the 'order' property
                          if (template.exercises && template.exercises.length > 0) {
                            template.exercises.sort((a, b) => {
                              const orderA = a.order === undefined ? Infinity : a.order;
                              const orderB = b.order === undefined ? Infinity : b.order;
                              // Primary sort by 'order'
                              if (orderA !== orderB) {
                                return orderA - orderB;
                              }
                              // Optional: Secondary sort by name if 'order' is the same or undefined for both
                              return a.name.localeCompare(b.name);
                            });

                            const firstExerciseTemplateDetails = template.exercises[0];
                            setCurrentWizardExerciseFormData({
                              name: firstExerciseTemplateDetails.name,
                              sets: firstExerciseTemplateDetails.defaultSets ?? 0,
                              reps: firstExerciseTemplateDetails.defaultReps ?? 0,
                              weight: firstExerciseTemplateDetails.defaultWeight ?? 0,
                              notes: firstExerciseTemplateDetails.notes ?? '',
                            });
                            setWizardExerciseData([]); // Reset data for the new template
                            setCurrentWizardExerciseIndex(0);
                            setWizardStep(1);
                            setWizardStartTime(Date.now()); // RECORD WIZARD START TIME HERE
                          } else {
                            setAlertMessage({type: 'warning', message: 'Diese Vorlage hat keine Übungen.'});
                          }
                        } else {
                          // wizardStep > 0, so we are in exercise configuration or finishing
                          handleWizardStepChange(template); // Pass the template here
                        }
                      }}
                      disabled={wizardStep === 0 && !selectedTemplateId}
                    >
                      {wizardStep === 0 ? 'Weiter zur ersten Übung' : 
                        (currentWizardExerciseIndex < (template?.exercises.length || 0) -1 ? `Nächste Übung (${currentWizardExerciseIndex + 1}/${template?.exercises.length})` : 'Workout Fertigstellen')
                      }
                    </Button>
                  </>
                );
              })()}
            </>
          ) : (
            // --- Existing Footer Buttons ---
            <>
              <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button variant="primary" onClick={handleSaveWorkout}>Speichern</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Workouts for Date Modal */}
      <Modal
        show={showWorkoutsForDateModal}
        onHide={() => setShowWorkoutsForDateModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Workouts am {selectedHeatmapDate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workoutsForSelectedDate.length > 0 ? (
            <div>
              {workoutsForSelectedDate.map((workout, index) => (
                <Card key={workout.id} className={index > 0 ? 'mt-3' : ''}>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Workout {index + 1}</span>
                    <div>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => {
                          setShowWorkoutsForDateModal(false);
                          setCurrentWorkout(workout);
                          setExercises(workout.exercises || []);
                          setShowModal(true);
                        }}
                      >
                        <FaEdit /> Bearbeiten
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Dauer:</strong> {workout.duration} Minuten</p>
                    {workout.notes && <p><strong>Notizen:</strong> {workout.notes}</p>}
                    <h6>Übungen:</h6>
                    <ListGroup variant="flush">
                      {workout.exercises && workout.exercises.map(exercise => (
                        <ListGroup.Item key={exercise.id}>
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>{exercise.name}</strong>
                            </div>
                            <div>
                              {exercise.sets} × {exercise.reps} @ {exercise.weight}kg
                            </div>
                          </div>
                          {exercise.notes && <small className="text-muted">{exercise.notes}</small>}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <Alert variant="info">Keine Workouts für dieses Datum gefunden.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWorkoutsForDateModal(false)}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Exercise Modal */}
      <Modal 
        show={showExerciseModal} 
        onHide={() => {
          setShowExerciseModal(false);
          setEditingExercise(null);
          setNewExercise({ id: '', name: '', sets: 3, reps: 10, weight: 0, notes: '' });
          setSelectedTemplateId('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingExercise ? 'Übung bearbeiten' : 'Neue Übung hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {!editingExercise && (
              <Form.Group className="mb-3">
                <Form.Label>Übung auswählen (Vorlage)</Form.Label>
                <Form.Select
                  value={selectedTemplateId}
                  onChange={(e) => handleExerciseTemplateChange(e.target.value)}
                >
                  <option value="">Manuell eingeben / Vorlage wählen...</option>
                  {exerciseTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.sets} Sätze x {template.reps} Wdh. @ {template.weight}kg)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type="text" 
                value={newExercise.name}
                onChange={(e) => setNewExercise({...newExercise, name: e.target.value})}
                placeholder="z.B. Bankdrücken"
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Sätze</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={newExercise.sets}
                    onChange={(e) => setNewExercise({
                      ...newExercise, 
                      sets: parseInt(e.target.value) || 0
                    })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Wiederholungen</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={newExercise.reps}
                    onChange={(e) => setNewExercise({
                      ...newExercise, 
                      reps: parseInt(e.target.value) || 0
                    })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Gewicht (kg)</Form.Label>
                  <Form.Control 
                    type="number" 
                    step="0.5"
                    value={newExercise.weight}
                    onChange={(e) => setNewExercise({
                      ...newExercise, 
                      weight: parseFloat(e.target.value) || 0
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notizen (optional)</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2}
                value={newExercise.notes || ''}
                onChange={(e) => setNewExercise({
                  ...newExercise, 
                  notes: e.target.value
                })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
              setShowExerciseModal(false); 
              setEditingExercise(null); 
              setNewExercise({ id: '', name: '', sets: 3, reps: 10, weight: 0, notes: '' });
              setSelectedTemplateId('');
          }}>
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={editingExercise ? handleUpdateExercise : handleAddExercise}
          >
            {editingExercise ? 'Übung aktualisieren' : 'Übung hinzufügen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WorkoutDashboard;

// CSS styles for the heatmap are in './styles.css'
/*
.react-calendar-heatmap .color-github-0 {
  fill: #ebedf0;
}
.react-calendar-heatmap .color-github-1 {
  fill: #c6e48b;
}
.react-calendar-heatmap .color-github-2 {
  fill: #7bc96f;
}
.react-calendar-heatmap .color-github-3 {
  fill: #239a3b;
}
.react-calendar-heatmap .color-github-4 {
  fill: #196127;
}
*/
